import { Component } from '@theme/component';

/**
 * Keys that browse the row, and keys that commit the browsing.
 *
 * A native radio group commits as it moves: a real arrow key fires `click`, `input` AND `change`
 * (measured in a browser, not assumed). For a control whose activation is a page load that is
 * wrong twice over — every arrow press would navigate, making the row impossible to browse from
 * the keyboard, and WCAG 3.2.2 (On Input) forbids changing context on a value change. So arrows
 * are cancelled and reimplemented as plain focus movement, leaving the checked state alone, and
 * navigation happens only on an explicit commit.
 *
 * @type {ReadonlySet<string>}
 */
const ARROW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/** Keys that activate the focused sibling. `' '` is Space; older engines report `'Spacebar'`. */
const COMMIT_KEYS = new Set(['Enter', ' ', 'Spacebar']);

/** Arrow keys that move backwards along the row. */
const BACKWARD_KEYS = new Set(['ArrowUp', 'ArrowLeft']);

/**
 * Reduces a path to what identifies the product: no query, no fragment, no trailing slash.
 *
 * @param {string} value - A path, possibly with a query string.
 * @returns {string} The bare path.
 */
const barePath = (value) => value.split('?')[0].split('#')[0].replace(/\/+$/, '');

/**
 * Resolves the sibling radio an event came from.
 *
 * Only the radio itself counts: clicking the wrapping `<label>` also forwards a click to its
 * input, so matching labels as well would handle the same activation twice.
 *
 * @param {EventTarget | null} target - The event target.
 * @returns {HTMLInputElement | null} The radio, or null when the event is not about one.
 */
const siblingRadioFrom = (target) => {
  if (!(target instanceof HTMLInputElement)) return null;

  return target.type === 'radio' && target.dataset.siblingUrl ? target : null;
};

/**
 * A custom element that turns a row of sibling products into a navigation control.
 *
 * Choosing a sibling is a navigation, not a variant change: there is nothing to re-render, the
 * chosen product's own page is loaded. That distinction drives the whole behaviour —
 *
 * - arrow keys move focus along the row without selecting or navigating;
 * - Enter or Space commit the focused sibling;
 * - pointer activation navigates immediately;
 * - the sibling already being viewed is a no-op.
 *
 * @extends {Component}
 */
export default class SiblingPicker extends Component {
  /**
   * Attaches the listeners.
   *
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('click', this.#handleClick);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * Removes the listeners.
   *
   * @returns {void}
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * Every sibling radio in the row, in document order.
   *
   * @returns {HTMLInputElement[]}
   */
  get #radios() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[type="radio"][data-sibling-url]')]);
  }

  /**
   * Whether a radio stands for the product already on screen.
   *
   * Derived from the URL, never from `data-current-checked`, which cannot be trusted here: the
   * theme's morph deliberately restores that attribute from the OLD node (assets/morph.js:48),
   * and the quick-add modal is one element reused for every product, morphing each new
   * `.product-details` over the last. Reading the flag there returns the PREVIOUS product's
   * answer — which made that product's chip a dead no-op and made the chip actually on screen
   * navigate to itself. `data-sibling-url` is not on morph's restore list, so it always describes
   * the markup currently rendered.
   *
   * In the quick-add modal nothing matches, since the page is the collection, so every chip
   * navigates — which is the right answer there.
   *
   * @param {HTMLInputElement} radio - A sibling radio.
   * @returns {boolean}
   */
  #isCurrent(radio) {
    const url = radio.dataset.siblingUrl?.trim();

    if (!url) return false;

    return barePath(url) === barePath(window.location.pathname);
  }

  /**
   * Sorts a key press into "browse the row" and "commit it".
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  #handleKeydown = (event) => {
    const radio = siblingRadioFrom(event.target);

    if (!radio) return;

    if (ARROW_KEYS.has(event.key)) {
      // Cancel the native move-and-select. Browsing must not change what is selected, or the row
      // would ring a sibling that is not the product being shown.
      event.preventDefault();
      this.#moveFocus(radio, BACKWARD_KEYS.has(event.key) ? -1 : 1);
      return;
    }

    if (!COMMIT_KEYS.has(event.key)) return;

    // Nothing else may act on this key — the navigation below is the whole commit. Space would
    // otherwise select the radio, and Enter can trigger implicit form submission.
    event.preventDefault();
    this.#navigate(radio);
  };

  /**
   * Navigates on pointer activation.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleClick = (event) => {
    const radio = siblingRadioFrom(event.target);

    if (radio) this.#navigate(radio);
  };

  /**
   * Re-selects the sibling that matches the page after a back/forward restore.
   *
   * A pointer click selects the radio before the page unloads. Coming back through the bfcache
   * restores the DOM as it was left, so without this the row would ring the sibling the shopper
   * clicked away to rather than the one they are looking at.
   *
   * @param {PageTransitionEvent} event - The pageshow event.
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    for (const radio of this.#radios) radio.checked = this.#isCurrent(radio);
  };

  /**
   * Moves focus along the row, wrapping at both ends, without selecting anything.
   *
   * @param {HTMLInputElement} from - The radio that has focus.
   * @param {number} step - `1` forwards, `-1` backwards.
   */
  #moveFocus(from, step) {
    const radios = this.#radios;
    const index = radios.indexOf(from);

    if (index === -1 || radios.length < 2) return;

    radios[(index + step + radios.length) % radios.length]?.focus();
  }

  /**
   * Loads the page of the sibling a radio stands for.
   *
   * @param {HTMLInputElement} radio - The radio carrying `data-sibling-url`.
   */
  #navigate(radio) {
    const url = radio.dataset.siblingUrl?.trim();

    if (!url || this.#isCurrent(radio)) return;

    window.location.assign(url);
  }
}

if (!customElements.get('sibling-picker')) {
  customElements.define('sibling-picker', SiblingPicker);
}
